import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useActor } from '@/app/auth';
import { prefetchRouteData, invalidateDataCache } from '@/app/data-cache';
import { subscribeSyncStatus, flushSyncQueue, type SyncStatusSnapshot } from '@/data/hybrid-sync';
import { useToast } from '@/app/toast';
import { APP_VERSION } from '@/app/app-version';
import { ThemeToggle } from '@/app/theme';
import { applyNavPrefs, useNavPrefs } from '@/app/nav-prefs';
import { playSound, soundForNotification, unlockAudio } from '@/app/sound';
import { useSmartScrollRestoration, PageTransition } from '@/app/experience-engine';
import { BrandIcon } from './BrandIcon';
import { Icon, type IconName } from './Icons';
import { Avatar } from './ui';
import { DeveloperCredit } from './DeveloperCredit';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  badge?: number;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function AppLayout({
  children,
  mode = 'company',
  platformName,
  platformLogo,
}: {
  children: ReactNode;
  mode?: 'company' | 'platform';
  platformName?: string;
  platformLogo?: string | null;
}) {
  const { snapshot, settings, can, canAny, unread, unreadChat, latestNotification, signOut, switchCompany, endPlatformSupportAccess, gateway, access, refresh } = useAuth();
  const toast = useToast();
  const actor = useActor();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('taskflow.ui.sidebar_collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [sidebarMini, setSidebarMini] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('taskflow.ui.sidebar_mini') === '1';
    } catch {
      return false;
    }
  });
  const toggleSidebarMini = (next?: boolean) => {
    setSidebarMini((prev) => {
      const value = typeof next === 'boolean' ? next : !prev;
      try {
        localStorage.setItem('taskflow.ui.sidebar_mini', value ? '1' : '0');
      } catch {
        // ignore
      }
      return value;
    });
  };
  const [openTopGroup, setOpenTopGroup] = useState<string | null>(null);
  const topNavCloseTimer = useRef<number | null>(null);
  const topNavRef = useRef<HTMLElement>(null);

  const toggleSidebarCollapsed = (next?: boolean) => {
    setSidebarCollapsed((prev) => {
      const value = typeof next === 'boolean' ? next : !prev;
      try {
        localStorage.setItem('taskflow.ui.sidebar_collapsed', value ? '1' : '0');
      } catch {
        // ignore
      }
      return value;
    });
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const online = useOnline();
  const [syncStatus, setSyncStatusSnapshot] = useState<SyncStatusSnapshot>({ online: typeof navigator !== 'undefined' ? navigator.onLine : true, pendingCount: 0, syncing: false, lastSyncedAt: null });

  useEffect(() => {
    const unsub = subscribeSyncStatus(setSyncStatusSnapshot);
    const onSyncCompleted = () => {
      invalidateDataCache();
      void refresh();
      toast.success('تمت مزامنة البيانات المسجلة محلياً ورفعها للسحابة تلقائياً');
    };
    window.addEventListener('taskflow:sync-completed', onSyncCompleted);
    return () => {
      unsub();
      window.removeEventListener('taskflow:sync-completed', onSyncCompleted);
    };
  }, [refresh, toast]);
  const navPrefs = useNavPrefs();
  const platformMode = mode === 'platform';
  const menuRef = useRef<HTMLDivElement>(null);
  const contextRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const lastNotificationRef = useRef<string | null>(null);

  useSmartScrollRestoration(contentRef);

  useEffect(() => {
    if (!actor?.companyId) return;
    const timer = window.setTimeout(() => {
      if (location.pathname !== '/' && location.pathname !== '/dashboard') {
        prefetchRouteData('/', gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds);
      }
      if (location.pathname !== '/tasks/assigned') {
        prefetchRouteData('/tasks/assigned', gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds);
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [actor, gateway, location.pathname, settings?.timeZone, access?.supervisedMembershipIds]);

  // فكّ حظر تشغيل الصوت بعد أول تفاعل (سياسة المتصفحات).
  useEffect(() => {
    let done = false;
    const unlock = () => {
      if (done) return;
      done = true;
      unlockAudio();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // صوت عند وصول إشعار جديد بعد التحميل الأول (لا نُصدر صوتًا للإشعارات القديمة).
  // إشعارات الشركة لا تُشغّل صوتًا داخل مساحة المنصة.
  useEffect(() => {
    if (platformMode || !latestNotification) return;
    if (lastNotificationRef.current === null) {
      lastNotificationRef.current = latestNotification.id;
      return;
    }
    if (lastNotificationRef.current === latestNotification.id) return;
    lastNotificationRef.current = latestNotification.id;
    playSound(soundForNotification(latestNotification.type));
  }, [latestNotification, platformMode]);

    // 100% Rock-Solid iOS & Android Mobile Scroll Lock for Sidebar
  useEffect(() => {
    if (!sidebarOpen) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const bodyStyle = document.body.style;
    const prevPosition = bodyStyle.position;
    const prevTop = bodyStyle.top;
    const prevLeft = bodyStyle.left;
    const prevRight = bodyStyle.right;
    const prevWidth = bodyStyle.width;
    const prevOverflow = bodyStyle.overflow;

    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.left = '0';
    bodyStyle.right = '0';
    bodyStyle.width = '100%';
    bodyStyle.overflow = 'hidden';

    const preventTouch = (e: TouchEvent) => {
      const target = e.target as HTMLElement | null;
      const sidebar = document.querySelector('.sidebar.open');
      if (!sidebar || !sidebar.contains(target)) {
        if (e.cancelable) e.preventDefault();
      }
    };

    window.addEventListener('touchmove', preventTouch, { passive: false });

    return () => {
      bodyStyle.position = prevPosition;
      bodyStyle.top = prevTop;
      bodyStyle.left = prevLeft;
      bodyStyle.right = prevRight;
      bodyStyle.width = prevWidth;
      bodyStyle.overflow = prevOverflow;
      window.scrollTo(0, scrollY);
      window.removeEventListener('touchmove', preventTouch);
    };
  }, [sidebarOpen]);

  useEffect(() => {
    setSidebarOpen(false);
    setMenuOpen(false);
    setContextOpen(false);
    setOpenTopGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
      if (contextRef.current && !contextRef.current.contains(event.target as Node)) setContextOpen(false);
      if (topNavRef.current && !topNavRef.current.contains(event.target as Node)) setOpenTopGroup(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const activeCompany = snapshot?.companies.find((company) => company.id === snapshot.activeCompanyId) ?? null;
  const activeMembership = snapshot?.memberships.find(
    (membership) => membership.companyId === snapshot?.activeCompanyId,
  );
  const profileName = snapshot?.platformSupportSession ? 'المنصة — دعم إداري'
    : (platformMode ? snapshot?.profile?.fullName : snapshot?.membershipProfile?.fullName) ?? snapshot?.session.email ?? '';
  const profileAvatar = snapshot?.platformSupportSession ? null
    : platformMode ? snapshot?.profile?.avatarDataUrl : snapshot?.membershipProfile?.avatarDataUrl;
  const sections: NavSection[] = [];

  if (platformMode) {
    const platformItems: NavItem[] = [
      { to: '/platform', label: 'نظرة عامة', icon: 'home', end: true },
    ];
    if (snapshot?.isPlatformOwner || can('platform.company.manage')) {
      platformItems.push({ to: '/platform/companies', label: 'المنشآت', icon: 'building' });
    }
    if (snapshot?.isPlatformOwner || can('platform.settings.manage')) {
      platformItems.push({ to: '/platform/settings', label: 'إعدادات المنصة', icon: 'settings' });
    }
    if (snapshot?.isPlatformOwner || can('platform.user.manage')) {
      platformItems.push({ to: '/platform/members', label: 'فريق المنصة', icon: 'users' });
    }
    if (snapshot?.isPlatformOwner || can('platform.audit.view')) {
      platformItems.push({ to: '/platform/activity', label: 'سجل النشاط', icon: 'book' });
    }
    if (snapshot?.isPlatformOwner) {
      platformItems.push({ to: '/platform/billing', label: 'الاشتراكات والمدفوعات', icon: 'chart' });
    }
    sections.push({ label: 'المنصة', items: platformItems });
  } else {
    const workItems: NavItem[] = [];
    if (can('task.view')) {
      if (snapshot?.platformSupportSession) {
        workItems.push(
          { to: '/', label: 'لوحة المنشأة', icon: 'home', end: true },
          { to: '/tasks/created', label: 'كل مهام المنشأة', icon: 'list' },
          { to: '/tasks/overdue', label: 'المتأخرة', icon: 'alert' },
        );
      } else {
        workItems.push(
          { to: '/', label: 'لوحة المهام', icon: 'home', end: true },
          { to: '/tasks/assigned', label: 'المسندة إليّ', icon: 'inbox' },
          { to: '/tasks/created', label: 'أنشأتها', icon: 'send' },
          { to: '/tasks/supervised', label: 'تحت إشرافي', icon: 'eye' },
          { to: '/tasks/personal', label: 'مهامي الشخصية', icon: 'lock' },
          { to: '/tasks/overdue', label: 'المتأخرة', icon: 'alert' },
        );
      }
    }
    if (workItems.length > 0) sections.push({ label: 'العمل', items: workItems });

    const opsFeatures = settings?.operationalFeatures;
    const opsItems: NavItem[] = [];
    if (!opsFeatures || opsFeatures.staffChatEnabled !== false) {
      opsItems.push({
        to: '/chat',
        label: 'الرسائل',
        icon: 'send',
        badge: unreadChat > 0 ? unreadChat : undefined,
      });
    }
    if (!opsFeatures || opsFeatures.bulletinsEnabled !== false) {
      opsItems.push({ to: '/bulletins', label: 'التعليمات والبيانات', icon: 'flag' });
    }
    if (!opsFeatures || opsFeatures.manualEnabled !== false) {
      opsItems.push({ to: '/manual', label: 'الدليل التشغيلي', icon: 'book' });
      opsItems.push({ to: '/staff-roles', label: 'دور كل موظف', icon: 'users' });
    }
    if (!opsFeatures || opsFeatures.shiftHandoverEnabled !== false) {
      opsItems.push({ to: '/handover', label: 'تسليم الوردية', icon: 'clock' });
    }
    if (!opsFeatures || opsFeatures.mediaHubEnabled !== false) {
      opsItems.push({ to: '/media', label: 'الملفات والوسائط', icon: 'paperclip' });
    }
    if (opsItems.length > 0) sections.push({ label: 'التواصل والتشغيل', items: opsItems });

    const planning: NavItem[] = [
      { to: '/leaderboard', label: 'لوحة الصدارة', icon: 'award' },
    ];
    if (can('task.template.manage')) planning.push({ to: '/templates', label: 'قوالب التكرار', icon: 'repeat' });
    if (can('report.view')) planning.push({ to: '/reports', label: 'التقارير', icon: 'chart' });
    if (planning.length > 0) sections.push({ label: 'المتابعة والتنافسية', items: planning });

    const opsEvolution: NavItem[] = [
      { to: '/assets', label: 'الأصول والمعدات', icon: 'layers' },
      { to: '/onboarding', label: 'التأهيل والتدريب', icon: 'book' },
      { to: '/automations', label: 'محرك الأتمتة', icon: 'zap' },
    ];
    sections.push({ label: 'الأصول والتطوير', items: opsEvolution });

    const isAdmin = canAny([
      'company.manage',
      'brand.manage',
      'branch.manage',
      'user.view',
      'membership.manage',
      'role.view',
      'supervision.manage',
      'task.category.manage',
      'company.settings.manage',
      'attachment.retention.manage',
      'audit.view',
      'platform.audit.view',
    ]);

    if (isAdmin) {
      const admin: NavItem[] = [];
      if (canAny(['company.manage', 'company.settings.manage', 'task.category.manage'])) {
        admin.push({ to: '/admin/company', label: 'الإعدادات', icon: 'settings' });
      }
      if (canAny(['attachment.retention.manage', 'company.settings.manage', 'company.manage'])) {
        admin.push({ to: '/admin/retention', label: 'التخزين والبيانات', icon: 'archive' });
      }
      if (canAny(['branch.manage', 'company.manage'])) {
        admin.push({ to: '/admin/branches', label: 'الفروع', icon: 'branch' });
      }
      if (canAny(['brand.manage', 'company.manage'])) {
        admin.push({ to: '/admin/brands', label: 'العلامات التجارية', icon: 'layers' });
      }
      if (canAny(['user.view', 'membership.manage'])) {
        admin.push({ to: '/admin/users', label: 'المستخدمون', icon: 'users' });
      }
      if (can('role.view')) admin.push({ to: '/admin/roles', label: 'الأدوار والصلاحيات', icon: 'shield' });
      if (can('supervision.manage')) {
        admin.push({ to: '/admin/supervision', label: 'الإشراف', icon: 'branch' });
      }
      if (canAny(['audit.view', 'platform.audit.view'])) {
        admin.push({ to: '/admin/audit', label: 'سجل النشاط', icon: 'book' });
      }
      if (admin.length > 0) sections.push({ label: 'إدارة المنشأة', items: admin });
    }
  }

  const footerItems: NavItem[] = platformMode
    ? [
        { to: '/platform/profile', label: 'الملف الشخصي', icon: 'user' },
        { to: '/platform/preferences', label: 'إعدادات الحساب', icon: 'settings' },
        { to: '/platform/about', label: 'عن التطبيق', icon: 'info' },
      ]
    : [
        { to: '/notifications', label: 'الإشعارات', icon: 'bell', badge: unread },
        { to: '/profile', label: 'الملف الشخصي', icon: 'user' },
        { to: '/settings', label: 'الإعدادات الشخصية', icon: 'settings' },
        { to: '/about', label: 'عن التطبيق', icon: 'info' },
      ];

  const visibleSections = platformMode
    ? sections
    : sections
        .map((section) => ({ ...section, items: applyNavPrefs(section.items, navPrefs) }))
        .filter((section) => section.items.length > 0);
  const visibleFooterItems = platformMode ? footerItems : applyNavPrefs(footerItems, navPrefs);

  const rawJobTitle = activeMembership?.jobTitle?.trim() ?? '';
  const localizedJobTitle =
    rawJobTitle.toLowerCase() === 'owner'
      ? 'مالك المنشأة'
      : rawJobTitle || (activeMembership?.isCompanyOwner ? 'مالك المنشأة' : 'مساحة العمل');
  const brandName = platformMode
    ? platformName?.trim() || 'إدارة المنصة'
    : activeCompany?.name ?? 'منصة المهام';
  const brandSubtitle = snapshot?.platformSupportSession
    ? 'وضع دعم المنصة'
    : platformMode
      ? 'مساحة إدارة مستقلة'
      : localizedJobTitle;
  const brandLogo = platformMode
    ? platformLogo ?? null
    : settings?.theme.logoDataUrl ?? null;
  const brandInitial = activeCompany?.name?.charAt(0) ?? 'م';
  const canAccessPlatform = Boolean(
    snapshot?.isPlatformOwner ||
      canAny([
        'platform.company.manage',
        'platform.settings.manage',
        'platform.audit.view',
        'platform.owner.manage',
      ]),
  );
  const showContextSwitcher = Boolean(
    snapshot && !snapshot.platformSupportSession &&
      (platformMode
        ? snapshot.memberships.length > 0
        : snapshot.memberships.length > 1 || canAccessPlatform),
  );

  // استنتاج القسم والصفحة الحالية لعرض مؤشر سياقي ذكي في الشريط العلوي بدل تكرار اسم المنشأة
  const activeRouteMeta = (() => {
    const path = location.pathname;
    for (const section of sections) {
      for (const item of section.items) {
        if (item.end ? path === item.to : path === item.to || path.startsWith(`${item.to}/`)) {
          return { section: section.label ?? null, label: item.label, icon: item.icon };
        }
      }
    }
    for (const item of footerItems) {
      if (path === item.to || path.startsWith(`${item.to}/`)) {
        return { section: 'حسابي', label: item.label, icon: item.icon };
      }
    }
    if (path === '/tasks/new') return { section: 'العمل', label: 'مهمة جديدة', icon: 'plus' as IconName };
    if (path.startsWith('/tasks/')) return { section: 'العمل', label: 'تفاصيل المهمة', icon: 'board' as IconName };
    if (path.startsWith('/templates/')) return { section: 'المتابعة', label: 'قالب مهمة', icon: 'repeat' as IconName };
    return {
      section: platformMode ? 'المنصة' : 'العمل',
      label: platformMode ? 'نظرة عامة' : 'لوحة المهام',
      icon: 'home' as IconName,
    };
  })();

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''} ${!sidebarCollapsed && sidebarMini ? 'sidebar-is-mini' : ''}`}>
      {sidebarOpen ? <div className="mobile-backdrop" onClick={() => setSidebarOpen(false)} onTouchMove={(e) => e.preventDefault()} /> : null}
      <aside onTouchMove={(e) => e.stopPropagation()} className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''} ${!sidebarCollapsed && sidebarMini ? 'mini' : ''}`} aria-label="التنقل الرئيسي">
        <div className="sidebar-brand">
          <div
            className={`brand-mark ${brandLogo ? 'has-logo' : ''} ${
              !brandLogo && platformMode ? 'has-brand-icon' : ''
            }`}
            aria-hidden="true"
            title={brandName}
          >
            {brandLogo ? (
              <img src={brandLogo} alt="" className="brand-logo" />
            ) : platformMode ? (
              <BrandIcon size={34} />
            ) : (
              brandInitial
            )}
          </div>
          <div className="sidebar-brand-copy grow">
            <div className="brand-name" title={brandName}><bdi>{brandName}</bdi></div>
            <div className="brand-sub"><bdi>{brandSubtitle}</bdi></div>
          </div>
          <div className="sidebar-brand-actions">
            <button
              type="button"
              className="btn btn-icon sidebar-rail-toggle"
              aria-label={sidebarMini ? 'توسيع القائمة الجانبية' : 'إظهار رموز القائمة فقط'}
              title={sidebarMini ? 'توسيع القائمة الجانبية (إظهار النصوص)' : 'تصغير القائمة (إظهار الرموز فقط)'}
              onClick={() => toggleSidebarMini()}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {sidebarMini ? (
                  <>
                    <line x1="3" y1="4" x2="3" y2="20" />
                    <polyline points="14 7 9 12 14 17" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </>
                ) : (
                  <>
                    <line x1="21" y1="4" x2="21" y2="20" />
                    <polyline points="10 17 15 12 10 7" />
                    <line x1="3" y1="12" x2="15" y2="12" />
                  </>
                )}
              </svg>
            </button>
            <button
              type="button"
              className="btn btn-icon sidebar-collapse-toggle"
              aria-label="إخفاء الشريط الجانبي"
              title="إخفاء الشريط الجانبي بالكامل"
              onClick={() => {
                if (window.matchMedia('(max-width: 860px)').matches) {
                  setSidebarOpen(false);
                } else {
                  toggleSidebarCollapsed(true);
                }
              }}
            >
              <Icon name="x" size={15} />
            </button>
          </div>
        </div>

        <nav className="nav">
          {visibleSections.map((section, index) => (
            <div key={section.label ?? index} className="nav-section-group">
              {section.label ? <div className="nav-group-label">{section.label}</div> : null}
              {section.items.map((item) => (
                <NavLink
                  key={item.to} to={item.to} end={item.end} title={item.label} onClick={() => { if (window.matchMedia('(max-width: 860px)').matches) setSidebarOpen(false); }} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  onMouseEnter={() => prefetchRouteData(item.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds)}
                  onTouchStart={() => prefetchRouteData(item.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds)}
                  onFocus={() => prefetchRouteData(item.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds)}
                >
                  <Icon name={item.icon} size={18} />
                  <span className="nav-link-text grow">{item.label}</span>
                  {item.badge ? <span className="nav-badge">{item.badge}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}

          <div className="nav-section-group">
            <div className="nav-group-label">حسابي</div>
            {visibleFooterItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.label}
                onClick={() => { if (window.matchMedia('(max-width: 860px)').matches) setSidebarOpen(false); }} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onMouseEnter={() => prefetchRouteData(item.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds)}
                onTouchStart={() => prefetchRouteData(item.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds)}
                onFocus={() => prefetchRouteData(item.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds)}
              >
                <Icon name={item.icon} size={18} />
                <span className="nav-link-text grow">{item.label}</span>
                {item.to === '/notifications' && unread > 0 ? <span className="nav-badge">{unread}</span> : null}
              </NavLink>
            ))}
          </div>

          {/* التوقيع ومعلومات التطبيق غير مثبتة — مرتبة ومنسقة بالكامل بدون انكسار أسطر أو تكرار */}
          <div className="sidebar-unpinned-footer">
            <div className="sidebar-status-meta">
              {snapshot?.isPlatformOwner ? (
                <span className="badge badge-primary" title="صلاحية إدارة المنصة">
                  مالك المنصة
                </span>
              ) : null}
              <span className="sidebar-meta-pill">
                <span
                  className={`sidebar-meta-dot ${gateway.kind === 'local' ? 'is-local' : 'is-cloud'}`}
                  aria-hidden="true"
                />
                <span>{gateway.kind === 'local' ? 'وضع التخزين المحلي' : 'تخزين سحابي ومحلي'}</span>
              </span>
              <span className="sidebar-meta-version">
                <bdi>v{APP_VERSION}</bdi>
                {typeof window !== 'undefined' && window.taskflowDesktop?.isDesktop ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>تطبيق ويندوز</span>
                  </>
                ) : null}
              </span>
            </div>
            <DeveloperCredit className="developer-credit-sidebar" hideVersion />
          </div>
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="btn btn-icon menu-toggle"
            aria-label="إظهار الشريط الجانبي"
            title="إظهار الشريط الجانبي"
            onClick={(e) => {
              e.stopPropagation();
              if (window.matchMedia('(max-width: 860px)').matches) {
                setSidebarOpen((prev) => !prev);
              } else {
                toggleSidebarCollapsed(false);
              }
            }}
          >
            <Icon name="menu" size={26} />
            <span className="menu-toggle-label">القائمة</span>
          </button>

          <div className="topbar-context">
            <span className="topbar-breadcrumb">
              <span className="topbar-breadcrumb-icon" aria-hidden="true">
                <Icon name={activeRouteMeta.icon} size={15} />
              </span>
              {activeRouteMeta.section ? (
                <>
                  <span className="topbar-breadcrumb-section hide-sm">{activeRouteMeta.section}</span>
                  <span className="topbar-context-sep hide-sm" aria-hidden="true">‹</span>
                </>
              ) : null}
              <span className="topbar-breadcrumb-current">{activeRouteMeta.label}</span>
            </span>
          </div>

          {/* حقل اسم المنشأة — مستقل ومنفصل عن إطار شريط التنقل بتصميم هوية مميز ومتناسق الأبعاد */}
          {showContextSwitcher && snapshot ? (
            <div className="dropdown-wrap topbar-brand-wrap" ref={contextRef}>
              <button
                type="button"
                className={`topbar-brand-card is-interactive ${contextOpen ? 'is-open' : ''}`}
                onClick={() => setContextOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={contextOpen}
                title="تبديل المنشأة أو مساحة العمل"
              >
                <span className="topbar-brand-emblem" aria-hidden="true">
                  {brandLogo ? (
                    <img src={brandLogo} alt="" className="topbar-brand-logo" />
                  ) : platformMode ? (
                    <Icon name="shield" size={14} />
                  ) : (
                    brandInitial
                  )}
                </span>
                <span className="topbar-brand-name">
                  <bdi>{platformMode ? 'إدارة المنصة' : activeCompany?.name ?? 'تبديل المساحة'}</bdi>
                </span>
                <Icon name="chevron-down" size={13} className="topnav-chevron" />
              </button>
              {contextOpen ? (
                <div className="dropdown" role="menu">
                  <div className="tiny muted" style={{ padding: '6px 10px' }}>
                    تبديل السياق
                  </div>
                  {canAccessPlatform ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="dropdown-item"
                      onClick={() => {
                        setContextOpen(false);
                        if (!platformMode) navigate('/platform');
                      }}
                    >
                      <Icon name="shield" size={15} />
                      <span className="grow">إدارة المنصة</span>
                      {platformMode ? <Icon name="check" size={15} /> : null}
                    </button>
                  ) : null}
                  {canAccessPlatform && snapshot.memberships.length > 0 ? (
                    <div className="dropdown-sep" />
                  ) : null}
                  {snapshot.memberships.map((membership) => {
                    const company = snapshot.companies.find((entry) => entry.id === membership.companyId);
                    return (
                      <button
                        key={membership.id}
                        type="button"
                        role="menuitem"
                        className="dropdown-item"
                        onClick={async () => {
                          setContextOpen(false);
                          if (membership.companyId !== snapshot.activeCompanyId) {
                            await switchCompany(membership.companyId);
                          }
                          navigate('/');
                        }}
                      >
                        <Icon name="building" size={15} />
                        <span className="grow">{company?.name ?? membership.companyId}</span>
                        {!platformMode && membership.companyId === snapshot.activeCompanyId ? (
                          <Icon name="check" size={15} />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : (sidebarCollapsed || sidebarMini) && (platformMode || activeCompany) ? (
            <div className="topbar-brand-card" title={platformMode ? 'إدارة المنصة' : activeCompany?.name}>
              <span className="topbar-brand-emblem" aria-hidden="true">
                {brandLogo ? (
                  <img src={brandLogo} alt="" className="topbar-brand-logo" />
                ) : platformMode ? (
                  <Icon name="shield" size={14} />
                ) : (
                  brandInitial
                )}
              </span>
              <span className="topbar-brand-name">
                <bdi>{platformMode ? 'إدارة المنصة' : activeCompany?.name}</bdi>
              </span>
            </div>
          ) : null}

          {/* شريط التنقل العلوي السلس والفاخر لشاشات الكمبيوتر والديسكتوب — يمتد بمرونة ليستغل المساحة بالكامل */}
          <nav
            className="desktop-topnav"
            aria-label="شريط التنقل العلوي"
            ref={topNavRef}
            onMouseLeave={() => {
              if (topNavCloseTimer.current) window.clearTimeout(topNavCloseTimer.current);
              topNavCloseTimer.current = window.setTimeout(() => setOpenTopGroup(null), 150);
            }}
            onMouseEnter={() => {
              if (topNavCloseTimer.current) window.clearTimeout(topNavCloseTimer.current);
            }}
          >

            {platformMode ? (
              visibleSections.flatMap((s) => s.items).map((item) => {
                const isItemActive = item.end
                  ? location.pathname === item.to
                  : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={`topnav-pill ${isItemActive ? 'active' : ''}`}
                    onMouseEnter={() => prefetchRouteData(item.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds)}
                  >
                    <Icon name={item.icon} size={15} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })
            ) : (
              <>
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) => `topnav-pill ${isActive ? 'active' : ''}`}
                  onMouseEnter={() => {
                    setOpenTopGroup(null);
                    prefetchRouteData('/', gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds);
                  }}
                >
                  <Icon name="home" size={15} />
                  <span>لوحة المهام</span>
                </NavLink>

                {visibleSections.map((section, idx) => {
                  const groupKey = section.label ?? `group-${idx}`;
                  const items = section.items.filter((i) => i.to !== '/');
                  if (items.length === 0) return null;
                  const activeItem = items.find((item) =>
                    item.end
                      ? location.pathname === item.to
                      : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
                  );
                  const totalBadge = items.reduce((sum, item) => sum + (item.badge ?? 0), 0);
                  const isOpen = openTopGroup === groupKey;
                  const leadIcon = activeItem?.icon ?? items[0]?.icon ?? 'layers';

                  if (items.length === 1) {
                    const single = items[0];
                    return (
                      <NavLink
                        key={single.to}
                        to={single.to}
                        end={single.end}
                        className={`topnav-pill ${activeItem ? 'active' : ''}`}
                        onMouseEnter={() => {
                          setOpenTopGroup(null);
                          prefetchRouteData(single.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds);
                        }}
                      >
                        <Icon name={single.icon} size={15} />
                        <span>{single.label}</span>
                        {single.badge ? <span className="topnav-badge">{single.badge}</span> : null}
                      </NavLink>
                    );
                  }

                  return (
                    <div
                      key={groupKey}
                      className={`topnav-group ${isOpen ? 'open' : ''}`}
                      onMouseEnter={() => {
                        if (topNavCloseTimer.current) window.clearTimeout(topNavCloseTimer.current);
                        setOpenTopGroup(groupKey);
                      }}
                    >
                      <button
                        type="button"
                        className={`topnav-pill ${activeItem ? 'active' : ''} ${isOpen ? 'is-open' : ''}`}
                        aria-haspopup="menu"
                        aria-expanded={isOpen}
                        onClick={() => setOpenTopGroup((prev) => (prev === groupKey ? null : groupKey))}
                      >
                        <Icon name={leadIcon} size={15} />
                        <span>{section.label}</span>
                        {activeItem ? (
                          <span className="topnav-active-sub">{activeItem.label}</span>
                        ) : null}
                        {totalBadge > 0 ? <span className="topnav-badge">{totalBadge}</span> : null}
                        <Icon name="chevron-down" size={13} className="topnav-chevron" />
                      </button>

                      {isOpen ? (
                        <div className="topnav-dropdown" role="menu">
                          <div className="topnav-dropdown-header">{section.label}</div>
                          <div className="topnav-dropdown-grid">
                            {items.map((item) => {
                              const isCurrent = item.end
                                ? location.pathname === item.to
                                : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                              return (
                                <NavLink
                                  key={item.to}
                                  to={item.to}
                                  end={item.end}
                                  role="menuitem"
                                  className={`topnav-dropdown-item ${isCurrent ? 'active' : ''}`}
                                  onMouseEnter={() => prefetchRouteData(item.to, gateway, actor, settings?.timeZone, undefined, access?.supervisedMembershipIds)}
                                  onClick={() => setOpenTopGroup(null)}
                                >
                                  <span className="topnav-item-icon">
                                    <Icon name={item.icon} size={16} />
                                  </span>
                                  <span className="topnav-item-label grow">{item.label}</span>
                                  {item.badge ? <span className="topnav-badge">{item.badge}</span> : null}
                                  {isCurrent ? <Icon name="check" size={14} /> : null}
                                </NavLink>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </>
            )}
          </nav>

          {/* أيقونات اليسار الأربع بحجم دائري موحد 36×36 ومسافة فاصلة متساوية تماماً */}
          <div className="topbar-actions">
            {!online ? (
              <span className="offline-pill" title="يعمل التطبيق الآن من التخزين المحلي، وسيتم رفع التعديلات تلقائياً فور عودة الإنترنت.">
                <Icon name="wifi-off" size={14} />
                وضع محلي{syncStatus.pendingCount > 0 ? ` (${syncStatus.pendingCount})` : ''}
              </span>
            ) : syncStatus.pendingCount > 0 ? (
              <button
                type="button"
                className="offline-pill"
                style={{ background: 'var(--primary-soft)', color: 'var(--primary-strong)', border: '1px solid var(--primary)', cursor: 'pointer' }}
                onClick={() => void flushSyncQueue()}
                title="اضغط لمزامنة البيانات المحلية مع السحابة الآن"
              >
                <Icon name="refresh" size={14} />
                {syncStatus.syncing ? `جاري المزامنة (${syncStatus.pendingCount})...` : `مزامنة (${syncStatus.pendingCount})`}
              </button>
            ) : null}

            <ThemeToggle />
            

            {!platformMode && !snapshot?.platformSupportSession && (!settings || settings.operationalFeatures.staffChatEnabled !== false) ? (
              <NavLink
                to="/chat"
                className="btn btn-icon topbar-circle-btn"
                aria-label="الرسائل والمحادثات"
                title="الرسائل والمحادثات"
              >
                <Icon name="send" size={17} />
                {unreadChat > 0 ? (
                  <span className="topbar-icon-badge badge-primary-solid">
                    {unreadChat > 99 ? '99+' : unreadChat}
                  </span>
                ) : null}
              </NavLink>
            ) : null}

            {!platformMode && !snapshot?.platformSupportSession ? (
              <NavLink
                to="/notifications"
                className="btn btn-icon topbar-circle-btn"
                aria-label="الإشعارات"
                title="الإشعارات"
              >
                <Icon name="bell" size={17} />
                {unread > 0 ? (
                  <span className="topbar-icon-badge badge-danger-solid">
                    {unread > 99 ? '99+' : unread}
                  </span>
                ) : null}
              </NavLink>
            ) : null}

            <div className="user-menu" ref={menuRef}>
              <button
                type="button"
                className="topbar-avatar-btn"
                onClick={() => setMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="قائمة المستخدم"
                title={profileName}
              >
                <Avatar name={profileName} src={profileAvatar} />
              </button>
              {menuOpen ? (
                <div className="dropdown dropdown-account" role="menu">
                  <div style={{ padding: '8px 10px' }}>
                    <div className="strong small">{profileName}</div>
                    <div className="tiny muted">{snapshot?.session.email}</div>
                  </div>
                  <div className="dropdown-sep" />
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => navigate(platformMode ? '/platform/profile' : '/profile')}
                  >
                    <Icon name="user" size={16} /> الملف الشخصي
                  </button>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => navigate(platformMode ? '/platform/preferences' : '/settings')}
                  >
                    <Icon name="settings" size={16} /> الإعدادات والخصوصية
                  </button>
                  {platformMode ? (
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => navigate('/platform/settings')}
                    >
                      <Icon name="shield" size={16} /> إعدادات المنصة
                    </button>
                  ) : null}
                  <div className="dropdown-sep" />
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={async () => {
                      await signOut();
                      navigate('/login');
                    }}
                  >
                    <Icon name="logout" size={16} /> تسجيل الخروج
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        {snapshot?.platformSupportSession && !platformMode ? (
          <div role="alert" className="support-mode-banner">
            <div className="grow"><strong>أنت تعمل الآن بوضع دعم المنصة داخل {activeCompany?.name ?? 'المنشأة'}.</strong><span className="small"> لا توجد عضوية موظف باسمك؛ كل العمليات تُسجّل باسم المنصة في سجل التدقيق المحمي، والإشعارات معطلة.</span></div>
            <button type="button" className="btn btn-sm btn-danger" onClick={async () => { try { await endPlatformSupportAccess(); navigate('/platform/companies'); } catch (cause) { window.alert(cause instanceof Error ? cause.message : 'تعذّر إنهاء جلسة الدعم'); } }}>إنهاء الدعم</button>
          </div>
        ) : null}
        <main className="content" ref={contentRef}>
          <PageTransition key={location.pathname}>
            {children}
          </PageTransition>
        </main>
      </div>
    </div>
  );
}
